import { useEffect } from 'react';
import { isTauri } from '../utils/tauri';
import { IS_COARSE_POINTER } from '../utils/pointer';
import { decideViewportFit, ViewportFitMode } from '../utils/viewportFit';

/** 진단 오버레이(KeyboardViewportDebug)가 읽는 마지막 판정 — 제품 동작에는 쓰지 않는다 */
let lastFit: { mode: ViewportFitMode | 'off'; bodyHeight: number; fixedHeight: number } = {
  mode: 'off',
  bodyHeight: 0,
  fixedHeight: 0,
};
export function getViewportFitDebug() {
  return lastFit;
}

/**
 * 모바일 웹/PWA에서 앱(#root)의 높이를 실제로 보이는 영역에 맞춘다. 판정은
 * `src/utils/viewportFit.ts`의 `decideViewportFit` 하나다(판정표: node scripts/test-viewport-fit.js).
 *
 * ① 키보드('keyboard') — 소프트 키보드가 layout viewport를 줄이지 못하는 브라우저(iOS Safari/PWA,
 * viewport meta의 interactive-widget을 모르는 구형 Android)에서 #root를 visualViewport 크기로 줄이고
 * 패닝을 따라가게 해 채팅 헤더(뒤로가기)가 화면 밖으로 밀리지 않게 한다. 키보드가 내려가면 원복.
 *
 * - iOS는 키보드가 뜨면 layout viewport는 그대로 두고 visual viewport를 아래로
 *   패닝해 포커스 입력을 드러낸다. 이 패닝은 프로그램으로 리셋할 수 없다 —
 *   문서 높이 ≤ layout viewport 높이면 스크롤 범위가 0이라 window.scrollTo는 no-op.
 *   그래서 싸우지 않고 translateY(vv.pageTop)로 추적한다(visual-viewport 고정 에뮬레이션).
 *   사용자가 페이지를 끌어 패닝해도 vv scroll 이벤트로 계속 따라붙는다(미세 jitter는
 *   이 기법의 알려진 한계).
 * - transform은 body가 아니라 #root에 건다. GlobalTooltip·ImageLightbox는
 *   "조상 transform이 position:fixed 기준을 깨므로 body로 포털 탈출"하는 구조라
 *   (각 파일 상단 주석 참조) body에 걸면 그 회피책이 무너진다. ContextMenu는 반대로
 *   body fixed가 iOS 키보드 패닝 중 좌표계가 어긋나는 사고를 겪고 #root 포털 +
 *   absolute로 옮겨 왔다 — 이 transform이 곧 그 배치의 기준이다. #root 안의 fixed
 *   요소(Modal·Toast 등)는 containing block이 보이는 영역으로 바뀌는데, 이는 회귀가
 *   아니라 개선이다(토스트가 키보드 위에 뜨고 모달이 보이는 영역에 정렬된다).
 * - interactive-widget=resizes-content가 적용된 Android Chrome 108+에서는 키보드가
 *   뜨면 layout viewport(ICB)가 함께 줄어 gap≈0이 되므로 이 분기는 저절로 걸리지 않는다 —
 *   UA 스니핑 없이 "layout viewport가 이미 줄어든 브라우저는 건드리지 않는다"가 성립한다.
 * - 키보드가 떠 있는 동안 `html.kb-open` 클래스를 세운다 — index.css가 이 마커로
 *   하단 safe-area 인셋 패딩을 접는다(키보드가 홈 인디케이터를 이미 덮고 있어 무의미).
 *
 * ② 키보드가 아닐 때('clamp') — 앱 컬럼(body의 CSS 100dvh)이 position:fixed 기준 상자보다 길면
 * #root 높이를 그 상자에 맞춘다(줄이기만, transform·kb-open 없음). 2026-11-05 안드로이드 설치 앱에서
 * 처음 불러올 때 100dvh가 보이는 창보다 크게 잡혀, 컬럼 맨 아래 형제인 하단 탭바(MobileTabBar —
 * fixed가 아니다)가 화면 밖으로 밀려났다. 같은 순간 fixed인 '새 대화' FAB는 정확히 보이는 하단에서
 * 84px 위에 있었다(스크린샷 실측) — fixed 기준 상자가 믿을 수 있는 쪽이다. 당김 새로고침은
 * location.reload()라 매번 처음 상태로 돌아가 "새로고침하면 사라진다"로 보였다.
 *   - 그 상자는 body 끝에 붙인 측정용 요소(`position:fixed; top:0; bottom:0`)로 잰다. #root 밖이라
 *     ①의 transform 영향을 받지 않는다
 *   - 그래서 vv 이벤트만이 아니라 **마운트 직후 1회 + 측정 요소·body ResizeObserver + pageshow**에도
 *     판정한다(늦게 적용되는 창 inset·dvh 재계산을 잡는다). #root 높이만 바꾸므로 관찰 대상(body·측정
 *     요소)의 크기는 변하지 않아 되먹임이 없다
 *   - iOS: 키보드가 없으면 fixed 상자 = 100dvh라 'none', 키보드가 뜨면 ①이 먼저 걸린다 — 동작 변화 없음
 */
export function useSoftKeyboardViewportFix(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || isTauri() || !IS_COARSE_POINTER) return; // 데스크톱·Tauri no-op
    const root = document.getElementById('root');
    if (!root) return;

    // fixed 기준 상자 측정용 — 화면에 아무것도 그리지 않는다
    const probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText =
      'position:fixed;top:0;bottom:0;left:0;width:0;visibility:hidden;pointer-events:none;';
    document.body.appendChild(probe);

    let mode: ViewportFitMode | 'off' = 'none';

    const apply = (next: 'keyboard' | 'clamp' | 'none', fixedHeight: number) => {
      if (next === 'keyboard') {
        // 인라인 style이라 #root의 Tailwind h-full을 자연히 이긴다
        root.style.setProperty('height', `${Math.round(vv.height)}px`);
        root.style.setProperty('transform', `translateY(${Math.round(vv.pageTop)}px)`);
        document.documentElement.classList.add('kb-open');
      } else if (next === 'clamp') {
        root.style.setProperty('height', `${Math.floor(fixedHeight)}px`);
        root.style.removeProperty('transform');
        // 키보드가 아니므로 하단 safe-area 패딩(홈 인디케이터)을 그대로 둔다
        document.documentElement.classList.remove('kb-open');
      } else if (mode !== 'none') {
        root.style.removeProperty('height');
        root.style.removeProperty('transform');
        document.documentElement.classList.remove('kb-open');
      }
      mode = next;
    };

    const update = () => {
      // ⚠ 키보드 판정 기준은 window.innerHeight가 아니라 documentElement.clientHeight(layout viewport 높이).
      // iOS Safari는 innerHeight가 visual viewport를 따라가므로 키보드가 뜨면 vv.height와
      // 함께 줄어들어 innerHeight 기준 gap은 항상 ≈0 — 훅이 영영 발동하지 않는다.
      // clientHeight는 iOS에서 키보드에 줄지 않고, resizes-content Android에서는 ICB와
      // 함께 줄어들어 "이미 줄어든 브라우저는 no-op" 규칙도 그대로 성립한다.
      const bodyHeight = document.body.getBoundingClientRect().height;
      const fixedHeight = probe.getBoundingClientRect().height;
      const next = decideViewportFit({
        clientHeight: document.documentElement.clientHeight,
        vvHeight: vv.height,
        scale: vv.scale ?? 1,
        bodyHeight,
        fixedHeight,
      });
      lastFit = { mode: next === 'skip' ? mode : next, bodyHeight, fixedHeight };
      if (next === 'skip') return; // 핀치줌 중에는 개입 금지 — 상태 유지
      apply(next, fixedHeight);
    };

    // resize: 키보드 개폐·회전 / scroll: 키보드가 뜬 동안 iOS가 visual viewport를 패닝할 때
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    // bfcache 복원 — 마운트가 다시 일어나지 않는다
    window.addEventListener('pageshow', update);
    // 늦게 적용되는 창 inset·dvh 재계산은 vv 이벤트 없이 body나 fixed 상자 크기만 바꿀 수 있다
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => update()) : null;
    ro?.observe(probe);
    ro?.observe(document.body);
    // 처음 불러온 직후의 어긋남은 아무 이벤트도 만들지 않는다 — 마운트 때 한 번 판정한다
    update();

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('pageshow', update);
      ro?.disconnect();
      apply('none', 0);
      probe.remove();
      lastFit = { mode: 'off', bodyHeight: 0, fixedHeight: 0 };
    };
  }, []);
}
